import React, { useContext, useState } from 'react';
import {
  FormattedMessage,
  useIntl,
} from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { graphql, Link, useStaticQuery } from 'gatsby';
import {
  Alert, Button, Spinner, Modal, TextareaField, Heading,
} from '@printdeal/ui-library';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';

import { ErrorManagement } from '@printdeal/error-management/react';
import LineItem from '../../../../../../types/cart/LineItem';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';

import { messages } from '../../../../translations/designService';
import OrderDetailContext from '../../../contexts/OrderDetailContext';
import { getChangeServiceLineItem } from '../../../../../../helpers/artworkHelper';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { Service } from '../../../../../../types/product/productDetailPage';
import { findNodeByLocale } from '../../../../../../helpers/translations';

const query = graphql`
  query {
    service: allContentfulService(filter: {name: {eq: "File Change"}}) {
      nodes {
        node_locale
        ...Service
      }
    }
  }`;

type QueryResult = {
  service: {
    nodes: Service[];
  };
};

interface RequestFileChangeModalProps {
  lineItem: LineItem;
  handleCloseModal: (eventName: string) => void;
}

const RequestFileChangeModal = ({
  lineItem,
  handleCloseModal,
}: RequestFileChangeModalProps) => {
  const queryClient = useQueryClient();
  // Load the order data from the context
  const { order, reFetchOrder } = useContext(OrderDetailContext);
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<null | { id: string, defaultMessage: string }>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const { service } = useStaticQuery<QueryResult>(query);
  const fileChangeService = findNodeByLocale(service.nodes, intl.locale);

  // Abort if no order data is available yet
  if (!order || !reFetchOrder) return null;

  const serviceCustomLineItem = getChangeServiceLineItem(lineItem.id, order);

  // Abort if no service data is
  if (!serviceCustomLineItem) return null;

  const ArtworkServiceFormik = useFormik({
    initialValues: {
      describeYourIdea: '',
      attachments: [],
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      dispatchAccountEvent('order_detail_interaction', 'modal_file_change_submit');

      if (attachments.length < 1) {
        setHasError(messages.noAttachment);
        setIsLoading(false);
      } else {
        const serviceService = ServiceFactory.createServices();
        const reqObj = {
          specification: values.describeYourIdea,
          orderId: order.id,
          lineItemId: lineItem.id,
          customLineItemId: serviceCustomLineItem.id,
          attachments,
        };
        try {
          const changeServiceResponse = await queryClient.fetchQuery({
            queryKey: ['order', 'submit-file-change-request', order.id, lineItem.id, JSON.stringify(reqObj)],
            queryFn: () => serviceService.createZendeskTicketChangeFile(reqObj),
          });

          if (!(changeServiceResponse instanceof Error) && !changeServiceResponse?.data?.success) {
            setHasError(messages.apiError);
          } else {
            await reFetchOrder({});
            handleCloseModal('model_file_change_close');
          }
        } catch (error) {
          ErrorManagement.exception({
            exception: error,
            tags: {
              fileLocation: 'ChangeServiceModal',
            },
            fingerprint: ['order-lineItem-service-fileChange-submit'],
          });
          setHasError(messages.apiError);
          dispatchAccountEvent('order_detail_api_failure', 'modal_file_change_submit');
        }

        setIsLoading(false);
      }
    },
  });

  const {
    handleSubmit,
    isSubmitting,
    getFieldProps,
    handleChange,
    values,
    setFieldValue,
  } = ArtworkServiceFormik;

  const alternativeUploadHandler = async (uploads: IAlternativeUpload[]) => {
    if (uploads) {
      uploads.map(({ urls: { originalUrl } }: any) => setAttachments([...attachments, originalUrl]));
      setHasError(null);
    }
    await setFieldValue('attachments', attachments);
  };

  return (
    <Modal
      isOpen
      onClose={() => handleCloseModal('modal_file_change_close')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'account.order.service.fileChange.modal.header',
        defaultMessage: 'Have a file change made',
      })}
    >
      <Modal.Content>
        {hasError && (
          <Alert variant="error" className="pd-mb-3">
            <FormattedMessage
              id={hasError.id}
              defaultMessage={hasError.defaultMessage}
            />
          </Alert>
        )}
        <div className="pd-my-3">
          <Heading size="3">
            <FormattedMessage
              id="account.order.service.fileChange.modal.body.title"
              defaultMessage="Tell us more about your desirable design improvements."
            />
          </Heading>
          <p className="pd-mt-3">
            <FormattedMessage
              id="account.order.service.fileChange.modal.body.title.info"
              defaultMessage="Our professional designers will get to work for you. Submit your files and detail your wishes for the design adjustment. With the following three steps we know enough for your design."
            />
          </p>
          {fileChangeService?.readMore?.path && (
          <p className="pd-text-dim-500">
            <FormattedMessage
              id="account.order.service.fileChange.modal.body.additional.info"
              defaultMessage="Do you want to know more about this design process? Check out our {page}"
              values={{
                page: (
                  <Link to={fileChangeService.readMore.path} className="pd-text-primary-600">
                    <FormattedMessage id="account.order.service.fileChange.modal.body.title.link" defaultMessage="Professional Online Design Adjustment page" />
                  </Link>
                ),
              }}
            />
          </p>
          )}
        </div>
        <form key="artworkService-form" id="artworkService-form" onSubmit={handleSubmit}>
          <TextareaField
            label={intl.formatMessage({
              id: 'account.order.service.fileChange.modal.body.label',
              defaultMessage: 'Describe your wishes',
            })}
            rows={3}
            className="pd-mb-4 pd-mt-2"
            id="describeYourIdea"
            {...getFieldProps('describeYourIdea')}
            onChange={handleChange}
            value={values.describeYourIdea || ''}
            placeholder={intl.formatMessage({
              id: 'account.order.service.fileChange.modal.body.placeholder',
              defaultMessage: 'Please describe here your wishes',
            })}
            required
          />
          <AlternativeUpload
            onChange={alternativeUploadHandler}
            isRequired
            id="file-change-request"
            name="file-change-request"
            dataLayerData={{ location: 'request_file_change_modal' }}
          />
        </form>
      </Modal.Content>
      <Modal.Footer
        className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2"
      >
        <Button
          variant="primary"
          form="artworkService-form"
          disabled={Boolean(hasError || isLoading || isSubmitting)}
          onClick={() => handleSubmit()}
        >
          {(isLoading || isSubmitting) && (
            <Spinner
              variant="dim"
              size="sm"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          )}
          <FormattedMessage
            id="account.order.service.any.modal.footer.submit"
            defaultMessage="Submit"
          />
        </Button>
        <Button variant="secondary" onClick={() => handleCloseModal('modal_file_change_close')}>
          <FormattedMessage
            id="account.order.service.any.modal.footer.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RequestFileChangeModal;
